import { Input, Node, Output, Socket } from 'rete'
import { BooleanTileGrid } from '../tile_grid'
import { workerPool } from '../../../modelling/workerPool'
import { NodeData, WorkerInputs, WorkerOutputs } from 'rete/types/core/data'
import { BaseComponent } from './base_component'
import { PreviewControl } from '../controls/preview'
import { BooleanControl } from '../controls/boolean'
import { isEqual } from 'lodash'
import { NumericConstant } from '../numeric_constant'
import { numericNumberDataSocket } from '../socket_types'
import { ProjectProperties } from '.'

const AND_EQUAL_TO_KEY = 'andEqualTo'

export class BinaryOpComponent extends BaseComponent {
  operator: string
  operation: string
  inputSocket: Socket
  outputSocket: Socket
  projectProperties: ProjectProperties
  supportsEqualTo: boolean

  constructor(operation: string, operator: string, inputSocket: Socket, outputSocket: Socket, category: string, projectProperties: ProjectProperties) {
    super(operation)
    this.operator = operator
    this.operation = operation
    this.inputSocket = inputSocket
    this.outputSocket = outputSocket
    this.category = category
    this.contextMenuName = operator ? `${operation} (${operator})` : operation
    this.projectProperties = projectProperties
    this.supportsEqualTo = operation === 'Less' || operation === 'Greater'
  }

  private effectiveOperator(andEqualTo: boolean): string {
    if (!andEqualTo || !this.supportsEqualTo) return this.operator
    if (this.operator === '<') return '<='
    if (this.operator === '>') return '>='
    return this.operator
  }

  private effectiveOperationName(andEqualTo: boolean): string {
    if (!andEqualTo || !this.supportsEqualTo) return this.name
    return `${this.operation}Equal`
  }

  private updateOutputLabel(node: Node, andEqualTo: boolean) {
    if (!this.operator) return
    const output = node.outputs.get('out')
    if (output) {
      output.name = `A ${this.effectiveOperator(andEqualTo)} B`
    }
  }

  async builder(node: Node) {
    node.addControl(new PreviewControl(() =>
      node.meta.output as any || new BooleanTileGrid(0, 0, 0, 1, 1)
    ))

    if (this.supportsEqualTo) {
      if (!(AND_EQUAL_TO_KEY in node.data)) {
        node.data[AND_EQUAL_TO_KEY] = false
      }
      const equalControl = new BooleanControl(this.editor, AND_EQUAL_TO_KEY, 'And equal to')
      const setValue = equalControl.props.setValue
      equalControl.props.setValue = (value: boolean) => {
        setValue(value)
        this.updateOutputLabel(node, value)
        // Bust the worker cache so the next engine run recomputes with the new op.
        delete node.meta.previousInputs
        node.update()
      }
      node.addControl(equalControl)
    }

    node.addInput(new Input('a', 'A', this.inputSocket))
    node.addInput(new Input('b', 'B', this.inputSocket))
    const andEqualTo = this.supportsEqualTo && Boolean(node.data[AND_EQUAL_TO_KEY])
    node.addOutput(new Output(
      'out',
      this.operator ? `A ${this.effectiveOperator(andEqualTo)} B` : `${this.operation}(A, B)`,
      this.outputSocket
    ))
  }

  async worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs, ...args: unknown[]) {
    const editorNode = this.editor?.nodes.find(n => n.id === node.id)
    if (editorNode === undefined) { return }

    const andEqualTo = this.supportsEqualTo && Boolean(node.data[AND_EQUAL_TO_KEY])
    this.updateOutputLabel(editorNode, andEqualTo)

    if (inputs['a'][0] === undefined || inputs['b'][0] === undefined) {
      editorNode.meta.errorMessage = 'Not enough inputs'
    }
    else if (isEqual([inputs['a'][0], inputs['b'][0], andEqualTo], editorNode.meta.previousInputs)) {
      delete editorNode.meta.errorMessage
      outputs['out'] = editorNode.meta.output
    }
    else {
      delete editorNode.meta.errorMessage
      editorNode.meta.previousInputs = [inputs['a'][0], inputs['b'][0], andEqualTo]

      const operationName = this.effectiveOperationName(andEqualTo)

      if (inputs['a'][0] instanceof NumericConstant && inputs['b'][0] instanceof NumericConstant && this.outputSocket === numericNumberDataSocket) {

        editorNode.meta.output = outputs['out'] = new NumericConstant(inputs['a'][0].performCalculation(this.name, inputs['b'][0]), undefined)

      } else {

        if (inputs['a'][0] instanceof NumericConstant) inputs['a'][0] = inputs['a'][0].asNumericTileGrid()
        if (inputs['b'][0] instanceof NumericConstant) inputs['b'][0] = inputs['b'][0].asNumericTileGrid()

        editorNode.meta.output = outputs['out'] = await workerPool.queue(async worker =>
          worker.performOperation(operationName, inputs['a'][0], inputs['b'][0])
        )
      }
    }

    if (outputs['out'] instanceof BooleanTileGrid || outputs['out'] instanceof NumericConstant) outputs['out'].name = (editorNode.data.name as string !== undefined && editorNode.data.name as string !== "") ? editorNode.data.name as string : `${this.operation} data`

    

    const previewControl: any = editorNode.controls.get('Preview')
    previewControl.update()
    editorNode.update()
  }
}
